/** A new report or manuscript: title, type and the sections to start with (an abstract is always added). */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MANUSCRIPT_SECTION_PRESETS, MANUSCRIPT_TYPE } from "@brainlog/shared";
import { errorMessage, post } from "@/lib/api";
import { qk } from "@/lib/queries";
import { openView } from "@/lib/store";
import type { Workspace } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createNoteIn } from "@/features/crypto/createNote";

export function NewManuscriptDialog({ workspace, onClose }: { workspace: Workspace; onClose(): void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [type, setType] = useState<string>("article");
  const [sections, setSections] = useState(MANUSCRIPT_SECTION_PRESETS.article.join("\n"));
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!edited) setSections((MANUSCRIPT_SECTION_PRESETS[type] ?? []).join("\n"));
  }, [type, edited]);

  const create = async () => {
    setBusy(true);
    try {
      const list = sections
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .slice(0, 40);
      const { manuscript, sectionsCreated } = await post<{ manuscript: { id: string; title: string; folderId: string | null }; sectionsCreated: boolean }>("/api/manuscripts", {
        workspaceId: workspace.id,
        title: title.trim(),
        type,
        sections: list,
      });
      // In an encrypted project the sections are created (encrypted) here.
      if (!sectionsCreated) {
        for (const [i, t] of ["Abstract", ...list].entries()) {
          const note = await createNoteIn(workspace, { folderId: manuscript.folderId, title: t, kind: "manuscript_section" });
          await post(`/api/manuscripts/${manuscript.id}/sections`, { noteId: note.id, role: i === 0 ? "abstract" : "section" });
        }
      }
      await Promise.all([qc.invalidateQueries({ queryKey: qk.manuscripts(workspace.id) }), qc.invalidateQueries({ queryKey: qk.tree(workspace.id) })]);
      onClose();
      openView("manuscript", workspace.id, manuscript.title, undefined, manuscript.id);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) void create();
          }}
        >
          <DialogHeader>
            <DialogTitle>New report or manuscript</DialogTitle>
            <DialogDescription>You write it on one page: an abstract, then one panel per section. Sections can be renamed, added or reordered later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="new-ms-title">Working title</Label>
            <Input id="new-ms-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus maxLength={500} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-ms-type">Type</Label>
            <select id="new-ms-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm">
              {MANUSCRIPT_TYPE.values.map((t) => (
                <option key={t} value={t}>
                  {MANUSCRIPT_TYPE.labels[t]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-ms-sections">Sections (one per line)</Label>
            <Textarea
              id="new-ms-sections"
              rows={6}
              value={sections}
              onChange={(e) => {
                setSections(e.target.value);
                setEdited(true);
              }}
            />
            <p className="text-xs text-muted-foreground">An abstract is always added first.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || busy}>
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
